import type { Kennzahl, GetValuesFn } from './types'

/**
 * Baut zwei gestapelte Bar-Datasets aus den Sub-Kennzahlen
 * `{kennzahlId}1.` und `{kennzahlId}2.`.
 *
 * Verwendung: 1.1., 2.1., 3.1. — ausschließlich für den Diagrammtyp BarChartStacked.
 * Für LineChart/BarChart wird im Aufrufer (ChartBoxItem) zu buildSubKennzahlChartData
 * gewechselt.
 */
export function buildStackedChartData(kennzahl: Kennzahl, getValues: GetValuesFn, baseYears: string[]): any {
    const prefix = kennzahl.sourceKennzahlId ?? kennzahl.kennzahlId
    const valuesOne = getValues(`${prefix}1.`)
    const valuesTwo = getValues(`${prefix}2.`)

    const dataOne: { x: string; y: number }[] = []
    const dataTwo: { x: string; y: number }[] = []
    let maxValue = 0

    valuesOne.forEach((v1) => {
        const match = valuesTwo.find((v2) => v2.semester === v1.semester)
        if (match) {
            dataOne.push({ x: v1.semester, y: Number(v1.wert) })
            dataTwo.push({ x: v1.semester, y: Number(match.wert) })
            maxValue = Math.max(Number(match.wert) + Number(v1.wert), maxValue)
        }
    })

    if (!dataOne.length) return false

    return {
        labels: baseYears,
        datasets: [
            {
                type: 'bar',
                label: kennzahl.datasetLabels?.[1] ?? '',
                data: dataOne,
                backgroundColor: '#004699',
                yAxisID: 'y1',
                maxValue
            },
            {
                type: 'bar',
                label: kennzahl.datasetLabels?.[2] ?? '',
                data: dataTwo,
                backgroundColor: '#009EE3',
                yAxisID: 'y1',
                maxValue
            }
        ]
    }
}

/**
 * Baut zwei separate Datasets (je eine Linie / Balkenserie) aus den Sub-Kennzahlen
 * `{kennzahlId}1.` und `{kennzahlId}2.` — für LineChart und BarChart.
 *
 * Wird in ChartBoxItem für strategy='stacked' + selectedType ≠ 'BarChartStacked' verwendet.
 */
export function buildSubKennzahlChartData(kennzahl: Kennzahl, getValues: GetValuesFn, baseYears: string[]): any {
    const prefix = kennzahl.sourceKennzahlId ?? kennzahl.kennzahlId
    const valuesOne = getValues(`${prefix}1.`)
    const valuesTwo = getValues(`${prefix}2.`)

    const dataOne = valuesOne.map((v) => ({ x: v.semester, y: Number(v.wert) }))
    const dataTwo = valuesTwo.map((v) => ({ x: v.semester, y: Number(v.wert) }))

    if (!dataOne.length && !dataTwo.length) return false

    return {
        labels: baseYears,
        datasets: [
            {
                label: kennzahl.datasetLabels?.[1] ?? '',
                data: dataOne,
                backgroundColor: '#004699',
                borderColor: '#004699',
                maxBarThickness: 41,
                minBarLength: 7
            },
            {
                label: kennzahl.datasetLabels?.[2] ?? '',
                data: dataTwo,
                backgroundColor: '#009EE3',
                borderColor: '#009EE3',
                maxBarThickness: 41,
                minBarLength: 7
            }
        ]
    }
}
